import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';
import { Col, Flex, Row, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { HomeOutlined } from '@ant-design/icons';
import { routePaths } from 'router';
import type { ClientRes, TWarehouseTaskRes, TWarehouseTaskType } from 'api/swagger';
import { useGetManyWarehouses } from 'api/warehouses/use-get-many-warehouses';
import { UserRole, useRoleGuard } from 'shared/hooks/use-role-guard.ts';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import { formatDateTime } from 'shared/utils/time/format-date-time.ts';
import { findWarehouseById } from 'shared/utils/warehouses/find-warehouse-by-id';
import { HiddenData } from 'components/hidden-data';
import { WhTaskStatusTag } from 'components/wh-tasks/wh-task-status-tag';
import { WhTaskTypeTag } from 'components/wh-tasks/wh-task-type-tag';
import { WhTaskDates } from 'components/wh-tasks/wh-tasks-list/components/wh-task-dates';
import type { IWhTasksListProps } from './types';
import styles from './wh-tasks-list.module.scss';

export const WhTasksList = (props: IWhTasksListProps) => {
  const { tasks, isLoading } = props;
  const { t } = useTranslation();

  const tableData = useTransformToTableData(tasks);

  const { data: warehouses } = useGetManyWarehouses();

  const isAllowFinancialInfo = useRoleGuard([
    UserRole.admin,
    UserRole.assistant,
    UserRole.customer_manager,
    UserRole.accountant,
  ]);

  const columns: ColumnsType<TWarehouseTaskRes> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '10%',
      render: (id: string, { compositeId, clientOrderNumber }) => (
        <Row align='middle' gutter={12}>
          <Col>
            <Link to={generatePath(routePaths.whTask, { whTaskId: id })}>{compositeId}</Link>
            <Typography.Text className={styles.secondary}>{clientOrderNumber}</Typography.Text>
          </Col>
        </Row>
      ),
    },
    {
      title: t('common:type'),
      dataIndex: 'type',
      width: '8%',
      render: (type: TWarehouseTaskType) => <WhTaskTypeTag type={type} />,
    },
    {
      title: `${t('common:warehouse')} / ${t('common:status')}`,
      dataIndex: 'warehouseId',
      width: '12%',
      render: (warehouseId, { status }) => (
        <Flex align='flex-start' gap={4} vertical>
          <Typography.Text>
            <HomeOutlined className={styles.tagIcon} /> {findWarehouseById(warehouseId, warehouses)?.name}
          </Typography.Text>
          <WhTaskStatusTag status={status} />
        </Flex>
      ),
    },
    {
      title: t('common:dates'),
      width: '15%',
      render: (_, record) => (
        <WhTaskDates
          type={record.type}
          arrivalDate={record.arrivalDate}
          departureDate={record.departureDate}
          realArrivalDate={record.realArrivalDate}
          realDepartureDate={record.realDepartureDate}
        />
      ),
    },
    {
      title: t('common:info'),
      width: '15%',
      render: (_, { totalLDM, totalVolume, totalWeight, numberOfItems, daysPerItem, status }) => {
        const shouldShowDaysPerItem = status === 'done' && daysPerItem !== null;

        return (
          <Flex vertical>
            <Typography.Text>
              {t('common:LDM')}: {totalLDM ?? '-'}
            </Typography.Text>
            <Typography.Text>
              {t('common:volume')}: {totalVolume ?? '-'} / {t('common:weight')}: {totalWeight ?? '-'}
            </Typography.Text>
            <Typography.Text>
              {t('common:pallets')}: {numberOfItems}
            </Typography.Text>
            {shouldShowDaysPerItem && (
              <Typography.Text>
                {t('wh-task:days_per_item')}: {daysPerItem}
              </Typography.Text>
            )}
          </Flex>
        );
      },
    },
    {
      title: t('order:freight'),
      width: '10%',
      render: (_, { freightInDefault, fineToUsInDefault, fineByUsInDefault }) => {
        if (!isAllowFinancialInfo) return <HiddenData />;
        return (
          <Flex vertical>
            <Typography.Text>{freightInDefault} €</Typography.Text>
            {fineToUsInDefault ? (
              <Typography.Text className={styles.fineToUs}>- {fineToUsInDefault} €</Typography.Text>
            ) : null}
            {fineByUsInDefault ? (
              <Typography.Text className={styles.fineByUs}>+ {fineByUsInDefault} €</Typography.Text>
            ) : null}
          </Flex>
        );
      },
    },
    {
      title: t('client:client'),
      dataIndex: 'client',
      width: '15%',
      render: (client: ClientRes) => (
        <Link to={generatePath(routePaths.client, { clientId: String(client.id) })}>
          {client?.shortName ?? client?.name}
        </Link>
      ),
    },
    {
      title: `${t('common:manager')} / ${t('common:created')}`,
      width: '15%',
      render: (_, { user, createdAt }) => (
        <Flex vertical>
          <Typography.Text>{user?.name}</Typography.Text>
          <Typography.Text className={styles.secondary}>{formatDateTime(createdAt)}</Typography.Text>
        </Flex>
      ),
    },
  ];

  return (
    <Table
      size={'small'}
      columns={columns}
      dataSource={tableData}
      pagination={false}
      loading={isLoading ? { size: 'large', delay: 200 } : false}
      bordered
    />
  );
};
